import type { ColDef } from "ag-grid-enterprise";
import { ForecastMetaDataType } from "../../../../hooks/useFetchForecastMetaData";
import { isPercentageMetric } from "../../../../utils/percentageMetrics";
import type { ForecastTableControlValues } from "../../../tableControls/forecastTableControlValues";
import { ChangeFlashCell } from "../../cellRenderers/ChangeFlashCell";
import {
  createMetricValueParser,
  type CubeWindow,
  formatCellValueByMetric,
  getCellStyle,
  setValueOrZero
} from "../../utils/columnUtils";
import { createDerivedValueGetter } from "../../utils/derivedUtils";
import {
  createIndexCellEditorSelector,
  createIndexValueFormatter,
  createIndexValueGetter,
  createIndexValueParser,
  createIndexValueSetter
} from "../../utils/indexUtils";
import {
  createShareValueGetter,
  shareValueFormatter
} from "../../utils/shareUtils";

/**
 * Column definitions that are common across weekly, monthly, quarterly and PTG.
 * Branches behavior based on planBy mode (ABS, IDX, +/- Var, SH%).
 */
export function baseTimePeriodProperties(
  planBy: ForecastTableControlValues["planBy"],
  planningYear: string,
  metaData: ForecastMetaDataType,
  cubeWindow?: CubeWindow,
  clockReplaced?: boolean
): Partial<ColDef> {
  // The index modes write the anchor row for a past year, so the dimming
  // gates that row — see ``getCellStyle``, which also reads ``clockReplaced``.
  const indexPlanningYear =
    planBy === "IDX" || planBy === "+/- Var" ? planningYear : undefined;
  const common: Partial<ColDef> = {
    type: "integer",
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    cellStyle: (params) =>
      getCellStyle(
        params,
        metaData,
        cubeWindow,
        indexPlanningYear,
        clockReplaced
      )
  };

  switch (planBy) {
    // Same relative flow, different display: IDX shows the index, "+/- Var"
    // the same number minus 100 with a "%" label.
    case "IDX":
    case "+/- Var":
      return {
        ...common,
        cellRenderer: ChangeFlashCell,
        valueGetter: createIndexValueGetter(planBy, planningYear, metaData),
        valueFormatter: createIndexValueFormatter(
          planBy,
          planningYear,
          metaData
        ),
        valueParser: createIndexValueParser(planBy, planningYear, metaData),
        valueSetter: createIndexValueSetter(planBy, planningYear, metaData),
        cellEditorSelector: createIndexCellEditorSelector(
          planBy,
          planningYear,
          metaData
        )
      };
    case "SH%":
      return {
        ...common,
        valueGetter: createShareValueGetter(metaData),
        valueFormatter: shareValueFormatter,
        editable: false
      };
    case "ABS":
    default:
      return {
        ...common,
        cellRenderer: ChangeFlashCell,
        valueGetter: createDerivedValueGetter(),
        valueFormatter: (params) => formatCellValueByMetric(params, metaData),
        // Rows are metrics, so the editor is picked per row: a percentage cell
        // seeds the editor with its own "60.54 %" label, so the unit shown is
        // the unit typed (see valueParsers).
        cellEditorSelector: (params) =>
          isPercentageMetric(
            params.data?.metric_name,
            metaData.metricFormatMapping
          )
            ? {
                component: "agTextCellEditor",
                params: { useFormatter: true }
              }
            : undefined,
        valueParser: createMetricValueParser(metaData),
        valueSetter: setValueOrZero
      };
  }
}
